/**
 * The locale every user-facing date/number label renders in.
 *
 * `Intl` and the `toLocale*` methods fall back to the *host's* default locale
 * when none is passed — the server's locale on Cloud Run, the browser's in the
 * dashboard. The product ships in English only, so an implicit locale doesn't
 * localise anything useful; it just makes the same row render differently per
 * viewer (and makes tests pass or fail depending on the developer's machine).
 *
 * Nothing outside this file should need to name it: reach for {@link dateFormat}
 * or {@link numberFormat} instead of passing it around. Switching it — or making
 * it per-user — is then one edit here rather than a sweep, which is the whole
 * point. Note this pins the *language*, not the timezone: timezone stays
 * implicit where the label is meant to read in local time.
 */
const DISPLAY_LOCALE = 'en-US'

const dateFormats = new Map<string, Intl.DateTimeFormat>()
const numberFormats = new Map<string, Intl.NumberFormat>()

/** Stable across key order, so the same shape written two ways shares an entry. */
function cacheKey(options: object): string {
  return JSON.stringify(options, Object.keys(options).sort())
}

/**
 * An `Intl.DateTimeFormat` in the display locale — the only sanctioned way to
 * build one.
 *
 * The locale is deliberately not a parameter. Every call site that constructed
 * its own formatter was a call site that could forget it, and fifteen of them
 * had (ClickUp `86d41v847`): dates rendered in the viewer's system language
 * beside English copy. Taking the choice away is what stops that recurring;
 * `apps/dashboard/eslint.config.js` bans the raw constructor to match.
 *
 * Instances are cached on their options, so callers don't need to hoist to
 * module scope for speed — constructing one is ~20-35x dearer than formatting
 * with it, and this makes every call after the first a Map lookup.
 *
 * Returns the formatter rather than a string so `.formatRange` and
 * `.formatToParts` callers share the one construction path.
 */
export function dateFormat(options: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat {
  const key = cacheKey(options)
  let format = dateFormats.get(key)
  if (!format) {
    format = new Intl.DateTimeFormat(DISPLAY_LOCALE, options)
    dateFormats.set(key, format)
  }
  return format
}

/** {@link dateFormat} for numbers — same reasoning, same caching. */
export function numberFormat(options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = cacheKey(options)
  let format = numberFormats.get(key)
  if (!format) {
    format = new Intl.NumberFormat(DISPLAY_LOCALE, options)
    numberFormats.set(key, format)
  }
  return format
}

/**
 * A plain grouped integer — "1,234".
 *
 * Worth a name of its own because a bare `n.toLocaleString()` is the shape this
 * bug takes on numbers, and it is much easier to miss than a month in Hebrew:
 * the digits stay readable, the separators just swap, so `1,234` quietly reads
 * as `1.234` to anyone on a European host.
 */
export function formatCount(value: number): string {
  return numberFormat().format(value)
}

export function getInitials(name: string | null): string {
  if (name) {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return '?'
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** "Jan 15, 2025" */
export function formatShortDate(iso: string): string {
  return dateFormat({ month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso))
}

/**
 * "Wed, July 15, 2026" — a coaching-call recording's day.
 *
 * Pinned to UTC because the recording's date is the *call's* date, which must
 * not slide a day for a viewer west of the boundary.
 */
export function formatRecordingDate(iso: string | null | undefined, fallback: string): string {
  if (!iso) return fallback
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return fallback
  return dateFormat({
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

const WEEK_RANGE_FORMAT = dateFormat({
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  // `periodStart` is a UTC date (YYYY-MM-DD). Formatting it in local time would
  // slide it a day backwards for anyone west of UTC — the Monday must read as
  // Monday everywhere.
  timeZone: 'UTC',
})

/**
 * The week starting at a Monday `YYYY-MM-DD` anchor — "Jul 6 – 12, 2026", or
 * "Dec 28, 2026 – Jan 3, 2027" across a year boundary. Intl collapses the shared
 * month/year itself, so a week inside one month doesn't repeat them.
 *
 * Intl pads the dash with thin spaces (U+2009); they're swapped for plain ones
 * so the label is greppable — tests and `getByText` selectors match on what's
 * typed, not on an invisible codepoint.
 */
export function formatWeekRange(periodStart: string): string {
  const start = new Date(`${periodStart}T00:00:00Z`)
  if (Number.isNaN(start.getTime())) return periodStart
  const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000)
  return WEEK_RANGE_FORMAT.formatRange(start, end).replace(/ /g, ' ')
}

const MONTH_LABEL_FORMAT = dateFormat({
  month: 'long',
  year: 'numeric',
  // A month key is a UTC anchor (YYYY-MM). Formatting it in local time would
  // slide it into the previous month for anyone west of UTC.
  timeZone: 'UTC',
})

/**
 * "May 2026" from a `YYYY-MM` month key. Falls back to the raw key for the
 * 'unknown' bucket the recordings importer assigns when no date can be parsed
 * from the title or created epoch.
 */
export function formatMonthLabel(monthKey: string): string {
  if (!/^\d{4}-\d{2}$/.test(monthKey)) return monthKey
  const [year, month] = monthKey.split('-').map((part) => Number.parseInt(part, 10))
  return MONTH_LABEL_FORMAT.format(new Date(Date.UTC(year, month - 1, 1)))
}

/** The date/time shape both formatters below render. They differ only in
 *  whether a zone is named in the output or applied to it. */
const DATE_TIME_PARTS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
} as const satisfies Intl.DateTimeFormatOptions

/**
 * {@link DATE_TIME_PARTS} to the second, in 24-hour time. Spreads the shared
 * shape rather than restating it, so the date half stays single-sourced.
 *
 * 24-hour is not a style preference, it is what pays for the seconds. See
 * {@link formatDateTimeWithSecondsInZone}.
 *
 * `hourCycle: 'h23'` and NOT `hour12: false`. The two are not equivalent:
 * `hour12` is resolved by the engine, and ours disagree. For `en-US`,
 * Chromium resolves `hour12: false` to h23 and renders midnight "00:00:00";
 * Node 20 / ICU 78 resolves it to h24 and renders "24:00:00". Naming the
 * cycle pins the same output in the browser and in vitest. Note `hour12`
 * wins over `hourCycle` when both are given, so it must stay absent.
 */
const DATE_TIME_SECONDS_PARTS = {
  ...DATE_TIME_PARTS,
  hour: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
} as const satisfies Intl.DateTimeFormatOptions

/** Shared null/NaN handling. Returns the parsed date, or null to use the fallback. */
function parseForDisplay(ts: string | Date | null | undefined): Date | null {
  if (!ts) return null
  const date = ts instanceof Date ? ts : new Date(ts)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "Jan 15, 2025, 3:45 PM PDT" — local time with timezone. Returns fallback when ts is null. */
export function formatDateTime(ts: string | Date | null | undefined, fallback = 'Unknown date'): string {
  const date = parseForDisplay(ts)
  if (!date) return fallback
  return dateFormat({ ...DATE_TIME_PARTS, timeZoneName: 'short' }).format(date)
}

/**
 * "Sep 10, 2026, 9:00 AM" — a datetime with no timezone suffix, in `timeZone`
 * when given and the viewer's zone otherwise.
 *
 * A sibling of {@link formatDateTime} rather than an option on it, because that
 * one has 39 call sites whose output would all shift. The activity log is the
 * only view that states its zone once in the page chrome instead of per row, so
 * it is the only one that can afford to drop the suffix (ClickUp `86d4b8axh`).
 *
 * **This no longer renders a table column.** It backed the activity log's two
 * time cells, and this comment used to argue the suffix off on column-width
 * grounds ("with it the widest reachable value is 27 characters and overflows,
 * without it 22"). Since `86d4c1c0t` those cells use
 * {@link formatDateTimeWithSecondsInZone}, and the only production caller left
 * is one sentence of page chrome in `AdminActivityLog.tsx`. The width argument
 * moved with the cells and now lives on that function and on `.colDateTime`.
 * What still applies here is why the suffix is off at all, which is that the
 * page states the zone once.
 *
 * `timeZone` is an IANA name (`Asia/Manila`), never a numeric offset, so DST
 * comes from the zone database. A stored offset cannot get a DST boundary
 * right, which is what makes this the right shape for the doer-timezone work
 * in `86d4b8ape`.
 */
export function formatDateTimeInZone(
  ts: string | Date | null | undefined,
  timeZone?: string,
  fallback = 'Unknown date',
): string {
  const date = parseForDisplay(ts)
  if (!date) return fallback
  return dateFormat({ ...DATE_TIME_PARTS, ...(timeZone ? { timeZone } : {}) }).format(date)
}

/**
 * "May 30, 2050, 00:00:00" — {@link formatDateTimeInZone} to the second, in
 * 24-hour time.
 *
 * The Activity Log's two time columns and nothing else (ClickUp `86d4c1c0t`).
 * A third sibling rather than an option on either existing formatter, for the
 * reason the second one already gives: `DATE_TIME_PARTS` is spread by
 * `formatDateTime` too, so a `second` added there reaches 39 call sites and
 * puts seconds on every timestamp in the product.
 *
 * **The 24-hour clock is load-bearing, not cosmetic.** Measured in Chromium
 * against the real VisbyCF-Regular at 14px, swept over 2026-2050 across every
 * month, day and full hh:mm:ss. Enumerating the year matters: Visby's figures
 * are proportional, so a single-year sweep understates the worst case by up to
 * 4.3px, which is how earlier revisions of this reasoning got it wrong.
 *
 * The 12-hour form "May 30, 2050, 10:00:00 AM" is 190.61px, which wants
 * `.colDateTime` at 14.5rem on both time columns. 14rem does fit it, by
 * 1.39px, and is rejected on headroom rather than overflow: 1.39px is under
 * the 2.5px thin-margin threshold recorded on `.colDateTime`. Dropping
 * " AM"/" PM" refunds 18.1px, bringing the worst case to
 * "May 30, 2050, 00:00:00" at 172.47px and holding the column at 13rem:
 * 3.53px of headroom, and 0 of the
 * 13,148,640 instants swept clip. Changing this back to 12-hour without
 * re-measuring will ship a truncated timestamp, which this table has done
 * once already.
 *
 * `timeZone` is an IANA name (`Asia/Manila`), never a numeric offset, for the
 * same DST reason as {@link formatDateTimeInZone}.
 */
export function formatDateTimeWithSecondsInZone(
  ts: string | Date | null | undefined,
  timeZone?: string,
  fallback = 'Unknown date',
): string {
  const date = parseForDisplay(ts)
  if (!date) return fallback
  return dateFormat({ ...DATE_TIME_SECONDS_PARTS, ...(timeZone ? { timeZone } : {}) }).format(date)
}

/**
 * The viewer's timezone as a short label ("GMT+8").
 *
 * For stating the zone once in page chrome, which is what lets
 * {@link formatDateTimeInZone} leave it off every row.
 */
export function viewerTimeZoneLabel(): string {
  const parts = dateFormat({ timeZoneName: 'short' }).formatToParts(new Date())
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
}

/**
 * The offset `timeZone` was at for the instant `ts`, as "GMT+8" or "GMT-4".
 *
 * For labelling the Activity Log's Doer time column (ClickUp `86d4bq6b9`).
 * Labelled by offset rather than by the IANA name, because the stored name is
 * right for the clock and wrong for a reader: Philippine Windows machines
 * report `Asia/Singapore`, and the API stores EST as `America/Panama`.
 *
 * `shortOffset`, not `short`. `short` gives "EDT" for New York and "UTC" for
 * UTC, which is the letter form this label exists to avoid. The answer is per
 * instant, because a DST zone has two offsets a year.
 *
 * Returns '' when `ts` does not parse.
 */
export function timeZoneOffsetLabel(ts: string | Date | null | undefined, timeZone: string): string {
  const date = parseForDisplay(ts)
  if (!date) return ''
  const parts = dateFormat({ timeZone, timeZoneName: 'shortOffset' }).formatToParts(date)
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
}

/**
 * Compact: "just now", "5m", "3h", "2d".
 * Verbose: "just now", "5 minutes ago", "3 hours ago", "2 days ago", then locale date for ≥7d.
 */
export function formatRelativeTime(iso: string, mode: 'compact' | 'verbose' = 'compact'): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.round(diff / 60_000)
  if (m < 1) return 'just now'
  if (mode === 'compact') {
    if (m < 60) return `${m}m`
    const h = Math.round(m / 60)
    if (h < 24) return `${h}h`
    const d = Math.round(h / 24)
    return `${d}d`
  }
  if (m < 60) return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} ${h === 1 ? 'hour' : 'hours'} ago`
  const d = Math.round(h / 24)
  if (d < 7) return `${d} ${d === 1 ? 'day' : 'days'} ago`
  return dateFormat().format(new Date(iso))
}

/** "5m 30s", "1h 15m" — human-readable duration */
export function formatHumanDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (minutes < 60) return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`

  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  const formatted = value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)
  return `${formatted} ${units[unitIndex]}`
}
